import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Boxes,
  Store,
  ClipboardList,
  Users,
  LogOut,
  Menu,
  X,
  Layers,
  ShoppingCart,
  ScrollText,
  HandCoins,
  PackageOpen,
  CreditCard,
  ArrowLeftRight,
  Undo2,
} from "lucide-react";
import { useState } from "react";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/inventory", label: "Stok Produk", icon: Boxes, testid: "nav-inventory" },
  { to: "/cards", label: "Stok Kartu & Slab", icon: CreditCard, testid: "nav-cards" },
  { to: "/pos", label: "Kasir (POS)", icon: ShoppingCart, testid: "nav-pos" },
  { to: "/trade", label: "Trade Kartu", icon: ArrowLeftRight, testid: "nav-trade" },
  { to: "/returns", label: "Refund & Retur", icon: Undo2, testid: "nav-returns" },
  { to: "/events", label: "Event Card Show", icon: Store, testid: "nav-events" },
  { to: "/preorders", label: "Pre-Order", icon: ClipboardList, testid: "nav-preorders" },
];

export function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = isAdmin ? [...navItems, { to: "/adjustments", label: "Penyesuaian Stok", icon: PackageOpen, testid: "nav-adjustments" }, { to: "/reimburse", label: "Rekap Reimburse", icon: HandCoins, testid: "nav-reimburse" }, { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit" }, { to: "/users", label: "Pengguna", icon: Users, testid: "nav-users" }] : navItems;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6 border-b border-slate-800">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <Layers className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-lg leading-none text-white">Novarium</h1>
            <p className="text-[10px] font-mono uppercase tracking-widest text-indigo-400 mt-1">Vault</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-indigo-500/15 text-indigo-300 shadow-sm"
                  : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-slate-800">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-sm font-bold text-indigo-300 uppercase">
            {user?.name?.[0] || "U"}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <span
              className={`text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded ${
                isAdmin ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"
              }`}
            >
              {user?.role}
            </span>
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-all"
        >
          <LogOut className="w-[18px] h-[18px]" />
          Keluar
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0E14] grain">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-[#0F131C] border-r border-slate-800 z-30 flex-col">
        <SidebarInner />
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 bg-[#0F131C]/95 backdrop-blur border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <Layers className="w-4 h-4 text-white" />
          </div>
          <span className="font-heading font-bold text-white">Novarium Vault</span>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="text-slate-300 p-2">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#0F131C] border-r border-slate-800" onClick={(e) => e.stopPropagation()}>
            <SidebarInner />
          </aside>
        </div>
      )}

      <main className="lg:pl-64 relative z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-8">{children}</div>
      </main>
    </div>
  );
}
